// ─────────────────────────────────────────────────────────────
// Centralized API Client
//
// All HTTP calls go through here. Each call declares which service
// it belongs to, so when a service is split into a microservice,
// you only update SERVICE_URLS in config.ts — nothing else changes.
//
// Usage:
//   api.get('discussions', '/discussions?sort=hot')
//   api.post('comments', '/discussions/:id/comments', body, token)
// ─────────────────────────────────────────────────────────────
import { SERVICE_URLS, ServiceName } from './config'

// Shape of error responses from our Fastify backend
interface ApiErrorBody {
  message?: string
  error?: string
  details?: Record<string, string[]>
  statusCode?: number
}

// Typed API error — carries the parsed response body so callers
// can display the backend's actual message instead of a generic string.
export class ApiError extends Error {
  status: number
  body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    // Build a readable message: prefer details array, then message, then fallback
    const detail = body.details
      ? Object.values(body.details).flat().join(', ')
      : null
    super(detail ?? body.message ?? `API error ${status}`)
    this.status = status
    this.body = body
  }
}

/**
 * Extract a human-readable error message from anything throw/rejected.
 * Works for ApiError, RTK Query FetchBaseQueryError, and plain Error.
 */
export function getErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof ApiError) {
    // Our typed error — message is already set correctly in the constructor
    return err.message
  }

  // RTK Query error shape: { status, data: { message, details } }
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>
    const data = e['data'] as ApiErrorBody | undefined
    if (data) {
      const detail = data.details ? Object.values(data.details).flat().join(', ') : null
      return detail ?? data.message ?? fallback
    }
    // Plain { message } object
    if (typeof e['message'] === 'string' && e['message']) return e['message']
  }

  return fallback
}

interface RequestOptions {
  method?: string
  body?: unknown
  token?: string | null
}

async function request<T>(
  service: ServiceName,
  path: string,
  options: RequestOptions = {},
  refreshToken?: string,
): Promise<T> {
  const { method = 'GET', body, token } = options
  const headers: Record<string, string> = {}
  if (body) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (refreshToken) headers['X-Refresh-Token'] = refreshToken

  const baseUrl = SERVICE_URLS[service]
  const res = await fetch(`${baseUrl}${path}`, {
    method,
    credentials: 'include',
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  })

  if (!res.ok) {
    let body: ApiErrorBody = {}
    try { body = await res.json() as ApiErrorBody } catch {}
    throw new ApiError(res.status, body)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get: <T>(service: ServiceName, path: string, token?: string | null) =>
    request<T>(service, path, { token }),

  post: <T>(service: ServiceName, path: string, body?: unknown, token?: string | null, refreshToken?: string) =>
    request<T>(service, path, { method: 'POST', body, token }, refreshToken),

  patch: <T>(service: ServiceName, path: string, body?: unknown, token?: string | null) =>
    request<T>(service, path, { method: 'PATCH', body, token }),

  put: <T>(service: ServiceName, path: string, body?: unknown, token?: string | null) =>
    request<T>(service, path, { method: 'PUT', body, token }),

  delete: <T>(service: ServiceName, path: string, token?: string | null) =>
    request<T>(service, path, { method: 'DELETE', token }),

  /**
   * Downloads a binary response (e.g. an exported report) and returns it as a
   * Blob plus the filename from Content-Disposition, for the caller to save.
   */
  downloadFile: async (service: ServiceName, path: string, token?: string | null): Promise<{ blob: Blob; filename: string | null }> => {
    const baseUrl = SERVICE_URLS[service]
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${baseUrl}${path}`, { credentials: 'include', headers })
    if (!res.ok) {
      let body: ApiErrorBody = {}
      try { body = await res.json() as ApiErrorBody } catch {}
      throw new ApiError(res.status, body)
    }
    const disposition = res.headers.get('Content-Disposition') ?? ''
    const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? null
    return { blob: await res.blob(), filename }
  },

  /**
   * Multipart file upload — does NOT set Content-Type so the browser
   * can add the correct multipart boundary automatically.
   */
  upload: async <T>(service: ServiceName, path: string, formData: FormData, token?: string | null): Promise<T> => {
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const baseUrl = SERVICE_URLS[service]
    const res = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    })
    if (!res.ok) {
      let body: ApiErrorBody = {}
      try { body = await res.json() as ApiErrorBody } catch {}
      throw new ApiError(res.status, body)
    }
    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
  },
}
